import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { AuthService } from '@core/services/auth.service';
import { BBIDService } from '@core/services/bbid.service';
import { DeepLinkingService } from '@core/services/deep-linking.service';
import { PortalDeterminationService } from '@core/services/portal-determination.service';
import { SpinnerService } from '@core/services/spinner.service';
import { TokenService } from '@core/services/token/token.service';
import { BbidLinkFailedReason, BbidLinkingResult } from '@core/typings/auth.typing';
import { environment } from '@environment';
import { I18nService } from '@yourcause/common/i18n';

@Component({
  selector: 'gc-bbid-signin',
  templateUrl: './bbid-signin.component.html',
  styleUrls: ['./bbid-signin.component.scss']
})
export class BbidSigninComponent implements OnInit {
  needToLinkBbid = this.activatedRoute.snapshot.queryParams.needToLinkBbid === 'true' ? true : false;
  email = this.activatedRoute.snapshot.queryParams.email;
  inSystem = this.activatedRoute.snapshot.queryParams.inSystem === 'true' ? true : false;
  initialRoute = this.activatedRoute.snapshot.queryParams.initialRoute;
  grantProgramGuid = this.activatedRoute.snapshot.queryParams.grantProgramGuid;
  bbidTiedToAnotherAccount = false;
  bbidEmail: string;
  failReason: BbidLinkFailedReason.BBID_LINKED_TO_ANOTHER_ACCOUNT|BbidLinkFailedReason.BBID_NON_LINKED_EMAIL_EXISTS_IN_GC;
  clientId: number;

  constructor (
    private activatedRoute: ActivatedRoute,
    private spinner: SpinnerService,
    private authService: AuthService,
    private router: Router,
    private deepLinkingService: DeepLinkingService,
    private portal: PortalDeterminationService,
    private bbidService: BBIDService,
    private tokenService: TokenService,
    private i18n: I18nService
  ) { }

  async ngOnInit () {
    this.spinner.startSpinner();
    const token = await this.bbidService.getToken();
    const parsed = this.tokenService.parseJwt(token);
    this.clientId = await this.authService.getSelectedClientForUser(this.email, parsed?.sub);
    await this.proceedWithLogin();
    this.spinner.stopSpinner();
  }

  async proceedWithLogin () {
    this.spinner.setLoadingMessage(this.i18n.translate(
      'common:textPleaseWaitWhileWeLogYouIn',
      {},
      'Please wait while we log you in'
    ));
    if (!!this.grantProgramGuid) {
      // If guid is included it means they are applying for a program
      // Set the deep link so after they are setup they land on the apply page
      this.deepLinkingService.setAttemptedRoute(`/apply/programs/${this.grantProgramGuid}`);
    }
    if (this.needToLinkBbid) {
      const {
        linkingResult,
        failReason
      } = await this.authService.handleLinkingBbidAccount(
        this.inSystem,
        this.clientId,
        this.email,
        '',
        this.grantProgramGuid
      );
      if (linkingResult === BbidLinkingResult.NEED_TO_SEND_CODE) {
        const route = this.portal.getRoute('auth/verification-code');
        const queryParams: Params = {
          isBbid: true,
          email: this.email,
          inSystem: this.inSystem,
          initialRoute: this.initialRoute,
          clientId: this.clientId
        };
        if (!!this.grantProgramGuid) {
          queryParams.grantProgramGuid = this.grantProgramGuid;
        }
        this.router.navigate([route], {
          queryParams
        });
      } else if (
        failReason === BbidLinkFailedReason.BBID_LINKED_TO_ANOTHER_ACCOUNT ||
        failReason === BbidLinkFailedReason.BBID_NON_LINKED_EMAIL_EXISTS_IN_GC
      ) {
        const token = await this.bbidService.getToken();
        const parsed = this.tokenService.parseJwt(token);
        this.bbidEmail = parsed.email;
        this.bbidTiedToAnotherAccount = true;
        this.failReason = failReason;
      }
    } else {
      await this.authService.loginWithBbid(
        environment.bbidGcServiceId,
        environment.bbidGcEnvironmentId,
        true,
        true,
        '',
        true,
        this.clientId
      );
    }
    this.spinner.setLoadingMessage('');
    this.spinner.stopSpinner();
  }

  signInToExistingBbidAccount () {
    this.email = this.bbidEmail;
    this.proceedWithLogin();
  }
}
